import type { BookingSlotsConfig } from '../models/Room';

/** UI fallback when `bookingSlotsConfig` is null (backend unrestricted). */
export const DEFAULT_UNRESTRICTED_BOOKING_SLOTS: BookingSlotsConfig = {
  minDurationInMinutes: 30,
  startTimeCardinalityInMinutes: 30,
  durationCardinalityInMinutes: 30,
};

/**
 * Default when enabling organization booking-slot constraints in the host UI.
 * Duration step 15 keeps 45-minute lessons valid.
 */
export const DEFAULT_ORG_BOOKING_SLOTS: BookingSlotsConfig = {
  minDurationInMinutes: 30,
  startTimeCardinalityInMinutes: 30,
  durationCardinalityInMinutes: 15,
};

/** @deprecated Use DEFAULT_ORG_BOOKING_SLOTS */
export const DEFAULT_TUTOR_BOOKING_SLOTS = DEFAULT_ORG_BOOKING_SLOTS;

export const BOOKING_SLOTS_MIN_DURATION_OPTIONS = [30, 60, 90, 120, 180, 240] as const;
export const BOOKING_SLOTS_START_STEP_OPTIONS = [15, 30, 60] as const;
export const BOOKING_SLOTS_DURATION_STEP_OPTIONS = [15, 30, 60, 90, 120, 240] as const;

export function getEffectiveBookingSlotsConfig(
  config: BookingSlotsConfig | null | undefined,
): BookingSlotsConfig {
  return config ?? DEFAULT_UNRESTRICTED_BOOKING_SLOTS;
}

export function cloneBookingSlotsConfig(config: BookingSlotsConfig): BookingSlotsConfig {
  return {
    minDurationInMinutes: config.minDurationInMinutes,
    startTimeCardinalityInMinutes: config.startTimeCardinalityInMinutes,
    durationCardinalityInMinutes: config.durationCardinalityInMinutes,
  };
}

export function bookingSlotsConfigsEqual(
  left: BookingSlotsConfig | null | undefined,
  right: BookingSlotsConfig | null | undefined,
): boolean {
  if (left == null && right == null) return true;
  if (left == null || right == null) return false;
  return (
    left.minDurationInMinutes === right.minDurationInMinutes &&
    left.startTimeCardinalityInMinutes === right.startTimeCardinalityInMinutes &&
    left.durationCardinalityInMinutes === right.durationCardinalityInMinutes
  );
}

/** Smallest duration that satisfies min + duration cardinality. */
export function firstValidDurationMinutes(config: BookingSlotsConfig): number {
  const { minDurationInMinutes, durationCardinalityInMinutes } = config;
  if (minDurationInMinutes % durationCardinalityInMinutes === 0) {
    return minDurationInMinutes;
  }
  return Math.ceil(minDurationInMinutes / durationCardinalityInMinutes) * durationCardinalityInMinutes;
}

export function isDurationAllowedByBookingSlotsConfig(
  durationMinutes: number,
  config: BookingSlotsConfig | null | undefined,
): boolean {
  if (!config) return true;
  return (
    Number.isInteger(durationMinutes) &&
    durationMinutes >= config.minDurationInMinutes &&
    durationMinutes % config.durationCardinalityInMinutes === 0
  );
}

export function buildDurationOptionsMinutes(
  config: BookingSlotsConfig | null | undefined,
  maxDurationMinutes = 360,
): number[] {
  const effective = getEffectiveBookingSlotsConfig(config);
  const step = effective.durationCardinalityInMinutes;
  const start = firstValidDurationMinutes(effective);
  const options: number[] = [];
  for (let duration = start; duration <= maxDurationMinutes; duration += step) {
    options.push(duration);
  }
  return options.length > 0 ? options : [start];
}

export function isStartTimeAlignedToBookingSlotsConfig(
  date: Date,
  config: BookingSlotsConfig | null | undefined,
): boolean {
  if (!config) return true;
  if (date.getSeconds() !== 0 || date.getMilliseconds() !== 0) return false;
  const minutesFromStartOfDay = date.getHours() * 60 + date.getMinutes();
  return minutesFromStartOfDay % config.startTimeCardinalityInMinutes === 0;
}

/** Round up to the next start aligned to cardinality from local midnight. */
export function ceilToStartTimeCardinality(date: Date, cardinalityMinutes: number): Date {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const minutesFromStart = (date.getTime() - dayStart.getTime()) / 60_000;
  const alignedMinutes = Math.ceil(minutesFromStart / cardinalityMinutes) * cardinalityMinutes;
  return new Date(dayStart.getTime() + alignedMinutes * 60_000);
}

export function formatFullCalendarSlotDuration(cardinalityMinutes: number): string {
  const safe = Math.max(1, Math.floor(cardinalityMinutes));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

export function filterLessonOptionsByBookingSlotsConfig<T extends { durationMinutes: number }>(
  options: T[],
  config: BookingSlotsConfig | null | undefined,
): T[] {
  if (!config) return options;
  return options.filter((option) =>
    isDurationAllowedByBookingSlotsConfig(option.durationMinutes, config),
  );
}

export function snapDurationToOptions(durationMinutes: number, options: number[]): number {
  if (options.length === 0) return durationMinutes;
  if (options.includes(durationMinutes)) return durationMinutes;
  const sorted = [...options].sort((a, b) => a - b);
  return sorted.find((option) => option >= durationMinutes) ?? sorted[sorted.length - 1];
}
